import { useState } from "react";
import { useAirdrops } from "../hooks/useAirdrops";
import { AirdropPicker } from "../components/AirdropPicker";
import { AirdropStatsPanel } from "../components/AirdropStatsPanel";
import { AirdropRecipientsTable } from "../components/AirdropRecipientsTable";
import { formatCards, formatDateRange } from "../utils/format";
import { Skeleton } from "../components/Skeleton";

export function Airdrops() {
  const airdropsQuery = useAirdrops();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const airdrops = airdropsQuery.data?.data ?? [];
  const activeId = selectedId ?? airdrops[0]?.id ?? null;
  const totals = airdrops.reduce(
    (acc, a) => ({
      distributed: acc.distributed + a.totalDistributed,
      recipients: acc.recipients + a.recipientCount,
    }),
    { distributed: 0, recipients: 0 }
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="page-title">$CARDS airdrops</h1>
          <p className="section-hint mt-1 max-w-2xl">
            Quarterly $CARDS distributions indexed from Solana: how much was
            handed out, to whom, and how it relates to the points gained that
            quarter. Open any wallet to compare it with similar-points wallets.
          </p>
        </div>
        {airdrops.length > 0 && (
          <div className="flex gap-3">
            <div className="card-inset p-3 min-w-36">
              <div className="text-gray-400 text-xs mb-1">All-time distributed</div>
              <div className="text-lg font-semibold text-yellow-400 num">
                {formatCards(totals.distributed)}
              </div>
            </div>
            <div className="card-inset p-3 min-w-36">
              <div className="text-gray-400 text-xs mb-1">Claims, all airdrops</div>
              <div className="text-lg font-semibold text-gray-50 num">
                {totals.recipients.toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </div>

      {airdropsQuery.isLoading && (
        <div className="card">
          <Skeleton kind="table" rows={4} />
        </div>
      )}

      {airdropsQuery.error && (
        <div className="card">
          <p className="text-red-400">
            Could not load airdrops: {String(airdropsQuery.error)}
          </p>
        </div>
      )}

      {!airdropsQuery.isLoading && airdrops.length === 0 && (
        <div className="card">
          <p className="text-gray-500">No $CARDS airdrops are being tracked yet.</p>
        </div>
      )}

      {airdrops.length > 0 && (
        <>
          {/* Quarter-by-quarter comparison */}
          <section className="card">
            <h2 className="section-title mb-4">Airdrops by quarter</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="table-head">
                    <th className="pb-3 pr-4 font-medium">Airdrop</th>
                    <th className="pb-3 pr-4 font-medium">Points period</th>
                    <th className="pb-3 pr-4 text-right font-medium">$CARDS distributed</th>
                    <th className="pb-3 pr-4 text-right font-medium">Recipients</th>
                    <th className="pb-3 pr-4 text-right font-medium">Avg per recipient</th>
                    <th className="pb-3 text-right font-medium">Last indexed</th>
                  </tr>
                </thead>
                <tbody>
                  {airdrops.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => setSelectedId(a.id)}
                      className={`table-row cursor-pointer ${
                        a.id === activeId ? "bg-gray-700/40" : ""
                      }`}
                    >
                      <td className="py-3 pr-4 font-medium text-gray-100">
                        {a.label}
                        {a.id === activeId && (
                          <span className="ml-2 pill bg-yellow-400/15 text-yellow-400">
                            selected
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-gray-400">
                        {formatDateRange(a.periodFrom, a.periodTo)}
                      </td>
                      <td className="py-3 pr-4 text-right font-semibold text-yellow-400 num">
                        {a.recipientCount > 0 ? formatCards(a.totalDistributed) : "—"}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-100 num">
                        {a.recipientCount > 0 ? a.recipientCount.toLocaleString() : "—"}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300 num">
                        {a.recipientCount > 0
                          ? formatCards(a.totalDistributed / a.recipientCount)
                          : "—"}
                      </td>
                      <td className="py-3 text-right text-gray-500 num">
                        {a.lastSyncedAt
                          ? new Date(a.lastSyncedAt).toLocaleDateString()
                          : "not indexed"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Selected airdrop: stats */}
          <section className="card space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="section-title">Airdrop stats</h2>
              <AirdropPicker airdrops={airdrops} activeId={activeId} onChange={setSelectedId} />
            </div>
            {activeId && <AirdropStatsPanel airdropId={activeId} />}
          </section>

          {/* Selected airdrop: recipients */}
          <section className="card space-y-4">
            <div>
              <h2 className="section-title">Recipients</h2>
              <p className="section-hint mt-1">
                Ranked by $CARDS received, with the points each wallet gained
                during the airdrop's points period.
              </p>
            </div>
            {activeId && (
              <AirdropRecipientsTable airdropId={activeId} showSummary={false} />
            )}
          </section>
        </>
      )}
    </div>
  );
}
